import { TFasterPayout } from '../../../../../api/src';
import {
  Badge,
  Table,
  TableContainer,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
} from '@chakra-ui/react';
import React from 'react';
import { NoData, ScrollBarWithFade } from '@payler/ui-components';
import { TableSkeleton } from '../../components/TableSkeleton/TableSkeleton';
import { PaymentDatesCell } from '../PaymentsTable/ReportDateCell';
import { AmountWithCurrencyCell } from '../PaymentsTable/AmountWithCurrencyCell';
import { useLocation, useNavigate } from 'react-router-dom';
import { PanAndIdCell } from './PanAndIdCell';
import { useDayjsFormat } from '@payler/utils';
import { BankNameWithLogo } from '../../components/BankNameWithLogo/BankNameWithLogo';
import { tableStyleConfig } from '../tableStyleConfig';
import { GroupRow } from '../PaymentsTable/GroupRow';
import { useURLContractId } from '../../layouts/ContractIdLayout/ContractIdLayout';
import { useTranslation } from 'react-i18next';
import { UseQueryResult } from '@tanstack/react-query';
import { TFasterPayoutsResponse } from '../../../../../api/src';
import { getFasterPayoutBadgeVariant } from './getFasterPayoutBadgeVariant';
import { getFasterPayoutStatusLabel } from './getFasterPayoutStatusLabel';

type GroupedFasterPayouts = TFasterPayoutsResponse & {
  grouped: Record<string, TFasterPayout[]>;
};

type Props = {
  useQuery: () => UseQueryResult<GroupedFasterPayouts>;
};

export const FasterPayoutsTableContent = ({ useQuery }: Props) => {
  const { status, data } = useQuery();

  switch (status) {
    case 'pending':
      return <TableSkeleton />;
    case 'error':
      return <NoData />;
    case 'success':
    default: {
      const empty = data.items.length === 0;
      if (empty) {
        return <NoData />;
      }
      return (
        <ScrollBarWithFade>
          <TableContainer position="relative">
            <Table styleConfig={tableStyleConfig}>
              <Header />
              <Tbody>
                {Object.entries(data.grouped)
                  .sort(([a], [b]) => -a.localeCompare(b))
                  .map(([group, rows]) => (
                    <Rows key={group} group={group} payments={rows} />
                  ))}
              </Tbody>
            </Table>
          </TableContainer>
        </ScrollBarWithFade>
      );
    }
  }
};

const Rows = ({
  group,
  payments,
}: {
  group: string;
  payments: TFasterPayout[];
}) => {
  const format = useDayjsFormat();
  return (
    <>
      <tr>
        <GroupRow colSpan={5}>{format(group, 'dddd DD MMMM')}</GroupRow>
      </tr>
      {payments.map((row) => (
        <Row data={row} key={row.id} />
      ))}
    </>
  );
};

const Row = ({ data }: { data: TFasterPayout }) => {
  const contractId = useURLContractId();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation(['fasterPayouts']);
  const handleClick = () => {
    if (contractId) {
      navigate(`/${contractId}/faster-payouts/${data.id}${location.search}`);
    }
  };
  return (
    <Tr onClick={handleClick}>
      <Td>
        <PaymentDatesCell
          topDate={data.created}
          bottomDate={data.lastOperationDate}
        />
      </Td>
      <Td>
        <Badge variant={getFasterPayoutBadgeVariant(data.status)}>
          {getFasterPayoutStatusLabel(data.status, t)}
        </Badge>
      </Td>
      <Td>
        <BankNameWithLogo {...data} />
      </Td>
      <Td>
        <PanAndIdCell id={data.merchantOrderId} pan={data.phone} />
      </Td>
      <Td textAlign="right">
        <AmountWithCurrencyCell
          amount={data.amount ?? 0}
          currencyCode={data.currencyCode}
          amountOfRefunds={0}
        />
      </Td>
    </Tr>
  );
};

const Header = () => {
  const { t } = useTranslation(['tableHeader']);
  return (
    <Thead>
      <Tr>
        <Th>{t('tableHeader:period')}</Th>
        <Th>{t('tableHeader:status')}</Th>
        <Th>{t('tableHeader:processingName')}</Th>
        <Th textAlign="right">{t('tableHeader:phoneAndOrderId')}</Th>
        <Th textAlign="right">{t('tableHeader:amount')}</Th>
      </Tr>
    </Thead>
  );
};
